/**
 * Kiosk and FPS-box choices of the Player (tablet on a stand, shown to visitors), saved per device
 * like the other settings. `?kiosk=1` / `?fps=1` in the Player's own URL win for that page; the
 * viewer gets the FPS choice as `fps=1` in its query and `walkbake.set({ fpsBox })` while open.
 *
 * Pure functions apart from storage, so they run under node:test.
 */

export interface KioskSettings {
  /** Hides the Player chrome (close, settings) except a discreet control; locks selection and gestures. */
  kiosk: boolean;
  /** Floating live FPS box inside the viewer. */
  fps: boolean;
}

export const DEFAULT_KIOSK_SETTINGS: KioskSettings = { kiosk: false, fps: false };

const STORAGE_KEY = "walk-player.kiosk";

function sanitize(raw: Partial<Record<keyof KioskSettings, unknown>>, fallback: KioskSettings): KioskSettings {
  return {
    kiosk: typeof raw.kiosk === "boolean" ? raw.kiosk : fallback.kiosk,
    fps: typeof raw.fps === "boolean" ? raw.fps : fallback.fps,
  };
}

export function loadKioskSettings(): KioskSettings {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return sanitize(JSON.parse(raw) as Partial<Record<keyof KioskSettings, unknown>>, DEFAULT_KIOSK_SETTINGS);
  } catch {
    // Blocked or corrupt storage: fall back to defaults.
  }
  return { ...DEFAULT_KIOSK_SETTINGS };
}

export function saveKioskSettings(settings: KioskSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // Not persisted; still applies for this session.
  }
}

/** "1" / "true" -> true, "0" / "false" -> false, anything else (or missing) -> undefined. */
export function parseFlag(value: string | null): boolean | undefined {
  if (value === "1" || value === "true") return true;
  if (value === "0" || value === "false") return false;
  return undefined;
}

/** `?kiosk=` and `?fps=` in the Player's URL win over the saved settings for that page; they are not saved. */
export function withKioskUrlOverrides(settings: KioskSettings, search: string): KioskSettings {
  const params = new URLSearchParams(search);
  return sanitize({ kiosk: parseFlag(params.get("kiosk")), fps: parseFlag(params.get("fps")) }, settings);
}
